import { Select, SelectProps } from 'antd';
import { Control, FieldValues, Path, useController } from 'react-hook-form';
import styled, { CSSProperties, css } from 'styled-components';

import { ArrowDownIcon } from 'assets/icons';
import EmployeeColumn from 'components/EmployeeColumn';

export type EmployeeFieldProps<T, K extends FieldValues> = Partial<SelectProps> & {
  name: Path<K>;
  control: Control<K>;
  options: T[];
  label?: string;
  defaultValue?: string | number;
  requiredIcon?: boolean;
  directionLabel?: 'horizontal' | 'vertical';
  labelStyle?: CSSProperties;
  isOrderPicking?: boolean;
};

interface StyledWrapperProps {
  directionLabel: 'horizontal' | 'vertical';
}

const StyledContainer = styled.div`
  .ant-select {
    width: 100%;
    .ant-select-selector {
      border-radius: 6px;
      border: 1px solid var(--cl_neutral300);
    }
  }
`;

const StyledWrapper = styled.div`
  ${(props: StyledWrapperProps) =>
    props?.directionLabel === 'horizontal' &&
    css`
      display: flex;
      align-items: center;
      gap: 8px;
    `}
`;

const StyledLabel = styled.div`
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--cl_gray900);
  ${(props: StyledWrapperProps) =>
    props?.directionLabel === 'vertical' &&
    css`
      margin-bottom: 6px;
    `}
`;

const StyledErrorMessage = styled.div`
  color: var(--cl_error500);
  margin-top: 6px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
`;

export function EmployeeField<T, K extends FieldValues>({
  name,
  control,
  label,
  options,
  defaultValue,
  directionLabel = 'vertical',
  labelStyle,
  requiredIcon = false,
  isOrderPicking = false,
  onChange: externalOnChange,
  onBlur: externalOnBlur,
  value: externalValue,
  ...rest
}: EmployeeFieldProps<T, K>) {
  const {
    field: { onBlur, onChange, value, ref },
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  return (
    <StyledContainer>
      <StyledWrapper directionLabel={directionLabel}>
        {label && (
          <StyledLabel style={labelStyle} directionLabel={directionLabel}>
            {label}
          </StyledLabel>
        )}

        <Select
          defaultValue={defaultValue ? defaultValue : undefined}
          value={value}
          ref={ref}
          status={error?.message ? 'error' : undefined}
          filterOption={(input, option) =>
            String(option?.label).toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
          suffixIcon={<ArrowDownIcon />}
          onChange={(value, option) => {
            onChange(value);
            externalOnChange?.(value, option);
          }}
          onBlur={onBlur}
          {...rest}
        >
          {options.map((option) => (
            <Select.Option key={option.value as string} value={option.value} label={option.label}>
              <EmployeeColumn
                avatar={option.label as string}
                fullName={option.label as string}
                email={option?.email}
              />
            </Select.Option>
          ))}
        </Select>
      </StyledWrapper>

      {error?.message && <StyledErrorMessage>{error?.message}</StyledErrorMessage>}
    </StyledContainer>
  );
}
